import FooterApp from '../ui/FooterApp';
import { Helmet } from 'react-helmet-async';
import Sidebar from '../ui/Sidebar';
import BottomNav from '../ui/BottomNav';
import { useEffect, useState } from 'react';
import { checkSession } from '@/utils/auth';
import Loading from '@/components/Loading';
import { NotificationSidebarProvider, useNotificationSidebar, Notification } from '@/contexts/NotificationSidebarContext';
import MessageSection from '../components/MessageSection';
import SignupTrialBanner from '../components/SignupTrialBanner';
import { clsx } from 'clsx';
import { apiGetRequest } from '@/utils/apiClient';
import CalendarScopeErrorModal from '@/components/CalendarScopeErrorModal';
import { notifyCalendarScopeError } from '@/utils/calendarScopeError';

/**
 * アプリログイン中の画面レイアウト
 *
 * 初回登録直後のモーダル表示の目安:
 * 1. セッション確立後、このコンポーネントで /api/settings/calendars を取得。一覧が空なら notify（1 セッション初回のみ）。
 * 2. FirstTimeExperienceManager（子ページ）: ウェルカム → チュートリアル（z-index 約 10 万）→ 登録フィードバック → ブックマーク案内。
 * 3. CalendarScopeErrorModal は z-index をチュートリアルより上げ、カレンダー未連携時は再連携案内を最前面にする。
 */
function PrivateLayoutContent({ children }: { children: React.ReactNode }) {
    const [isCheckingSession, setIsCheckingSession] = useState(true);
    const { isOpen: isSidebarOpen, closeSidebar, setHasUnread, notifications, setNotifications } = useNotificationSidebar();

    // メイン処理
    useEffect(() => {
        // セッションチェック
        const check = async () => {
            try {
                await checkSession();
            } catch (error) {
                // checkSession内でリダイレクト処理が行われるため、エラーハンドリングは不要
            } finally {
                setIsCheckingSession(false);
            }
        };
        check();
    }, []);

    useEffect(() => {
        if (isCheckingSession) return;
        const loadCalendars = async () => {
            try {
                const res = await apiGetRequest('/api/settings/calendars');
                if (!res.ok) return;
                const data: unknown = await res.json();
                const rawList =
                    data && typeof data === 'object' && 'calendars' in data
                        ? (data as { calendars: unknown }).calendars
                        : [];
                if (!Array.isArray(rawList) || rawList.length > 0) return;
                notifyCalendarScopeError();
            } catch {
                // カレンダー確認失敗は致命的にしない（未読バッジ等と同様）
            }
        };
        void loadCalendars();
    }, [isCheckingSession]);

    /**
     * 初期表示時に未読状態を取得
     * 右サイドバーが閉じている時のみ、かつ通知データが未取得の場合のみ実行
     */
    useEffect(() => {
        const fetchUnreadStatus = async () => {
            // 右サイドバーが開いている場合は、NotificationListが取得するのでスキップ
            if (isSidebarOpen) {
                return;
            }

            // 既にデータがある場合はスキップ（重複を避ける）
            if (notifications.length > 0) {
                return;
            }

            try {
                const response = await apiGetRequest('/api/notifications');

                if (!response.ok) {
                    // 429エラーの場合は特別に処理（ログのみ）
                    if (response.status === 429) {
                        console.warn('Too many requests when fetching unread status');
                        return;
                    }
                    // その他のエラーは無視（未読バッジは表示されないだけ）
                    return;
                }

                const data: Notification[] = await response.json();
                
                // 未読のお知らせがあるかチェック
                const hasUnread = data.some((n) => !n.read);
                setHasUnread(hasUnread);
                
                // データも保存（右サイドバーを開いた時に再取得不要にするため）
                setNotifications(data);
            } catch (err) {
                // エラー時はログのみ（未読バッジは表示されないだけ）
                console.error('Error fetching unread status:', err);
            }
        };

        // セッションチェック完了後に未読状態を取得
        if (!isCheckingSession) {
            fetchUnreadStatus();
        }
    }, [isCheckingSession, isSidebarOpen, notifications.length, setHasUnread, setNotifications]);

    // セッションチェック中はローディング画面を表示
    if (isCheckingSession) {
        return <Loading />;
    }

    return (
        <>
            <Helmet>
                <meta name="robots" content="noindex,nofollow" />
            </Helmet>
            <CalendarScopeErrorModal />

            <div className="flex min-h-screen overflow-hidden overflow-x-hidden">
                {/* 左サイドバー（PCのみ表示） */}
                <div className="hidden lg:block">
                    <Sidebar />
                </div>
                
                {/* コンテンツエリア */}
                <div className="flex flex-1 lg:ml-48 relative">
                    {/* メインコンテンツ */}
                    <div
                        className={clsx(
                            "flex flex-col flex-1 pb-0 transition-all duration-300",
                            isSidebarOpen ? "lg:mr-[400px]" : "lg:mr-0",
                        )}
                    >
                        <SignupTrialBanner isMessageSidebarOpen={isSidebarOpen} />
                        {children}
                        <FooterApp />
                    </div>

                    {/* 右サイドバー（メッセージセクション） */}
                    <MessageSection isOpen={isSidebarOpen} onClose={closeSidebar} />
                </div>

                {/* ボトムナビゲーション（モバイルのみ表示） */}
                <div className="lg:hidden" style={{ boxShadow: '0 -4px 6px rgba(0,0,0,0.8)' }}>
                    <BottomNav />
                </div>
            </div>
        </>
    );
}

function PrivateLayout({ children }: { children: React.ReactNode }) {
    return (
        <NotificationSidebarProvider>
            <PrivateLayoutContent>{children}</PrivateLayoutContent>
        </NotificationSidebarProvider>
    );
}

export default PrivateLayout;
